import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/index.js';
import { API_BASE_URL } from '../config.js';
import { useToast } from '../context/ToastContext.js';
import './GoalSetting.css';

const APPARATUS = [
  { id: 'vault',      label: 'Vault',        emoji: '🏃', color: '#FF6B6B' },
  { id: 'floor',      label: 'Floor',        emoji: '🤸', color: '#4ECDC4' },
  { id: 'bars',       label: 'Uneven Bars',  emoji: '💪', color: '#FFE66D' },
  { id: 'beam',       label: 'Balance Beam', emoji: '🧘', color: '#95E1D3' },
  { id: 'trampoline', label: 'Trampoline',   emoji: '⛹️', color: '#A8E6CF' },
  { id: 'general',    label: 'General',      emoji: '⭐', color: '#9D84B7' },
];

const DIFFICULTY = [
  { id: 'beginner',     label: 'Beginner',     icon: '🟢' },
  { id: 'intermediate', label: 'Intermediate', icon: '🟡' },
  { id: 'advanced',     label: 'Advanced',     icon: '🟠' },
  { id: 'elite',        label: 'Elite',        icon: '🔴' },
];

const TODAY = new Date().toISOString().split('T')[0];

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const diff = new Date(dateStr + 'T00:00:00') - new Date(new Date().toDateString());
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

function apparatusFor(category) {
  return APPARATUS.find(a => a.id === category) || APPARATUS[5];
}

// JSON columns (strategies/obstacles) may arrive parsed (mysql2) or as a string.
function asArr(v) {
  if (Array.isArray(v)) return v;
  try { const p = JSON.parse(v || '[]'); return Array.isArray(p) ? p : []; } catch { return []; }
}

// --- Celebration overlay ---
function CelebrationOverlay({ title, onDismiss }) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 5000);
    return () => clearTimeout(t);
  }, [onDismiss]);

  return (
    <div className="celebration-overlay" onClick={onDismiss}>
      <div className="celebration-box" onClick={e => e.stopPropagation()}>
        <div className="celebration-stars">
          {['⭐','🌟','✨','🎉','🏆','⭐','🌟','✨','🎊','💫'].map((s, i) => (
            <span key={i} className={`star-burst star-${i}`}>{s}</span>
          ))}
        </div>
        <div className="celebration-trophy">🏆</div>
        <h2 className="celebration-title">Goal Achieved!</h2>
        <p className="celebration-goal-name">"{title}"</p>
        <p className="celebration-message">You crushed it! Keep setting goals and pushing your limits.</p>
        <button className="celebration-dismiss" onClick={onDismiss}>Keep Going! 💪</button>
      </div>
    </div>
  );
}

// --- Due-soon chip ---
function DueChip({ targetDate, status }) {
  if (!targetDate || status === 'achieved' || status === 'exceeded') return null;
  const days = daysUntil(targetDate);
  if (days === null) return null;
  if (days < 0) return <span className="due-chip overdue">Overdue</span>;
  if (days === 0) return <span className="due-chip due-today">Due Today</span>;
  if (days <= 7) return <span className="due-chip due-soon">Due in {days}d</span>;
  return null;
}

// --- GoalCard ---
function GoalCard({ goal, userRole, expanded, onToggle, onProgressUpdate, onDelete }) {
  const app = apparatusFor(goal.category);
  const [localPct, setLocalPct] = useState(goal.progress_percentage || 0);
  const [saving, setSaving] = useState(false);
  const canEdit = userRole === 'athlete';
  const isAchieved = goal.status === 'achieved' || goal.status === 'exceeded';

  useEffect(() => { setLocalPct(goal.progress_percentage || 0); }, [goal.progress_percentage]);

  const handleSave = async () => {
    setSaving(true);
    await onProgressUpdate(goal.id, localPct);
    setSaving(false);
  };

  const statusLabel = { not_started: 'Not Started', in_progress: 'In Progress', achieved: 'Achieved', exceeded: 'Exceeded!' };
  const statusClass = { not_started: 'status-not-started', in_progress: 'status-in-progress', achieved: 'status-achieved', exceeded: 'status-exceeded' };

  return (
    <div className={`goal-card ${isAchieved ? 'goal-card-achieved' : ''}`}>
      <div className="goal-card-header" style={{ backgroundColor: app.color }} onClick={onToggle}>
        <span className="goal-card-emoji">{isAchieved ? '🏆' : app.emoji}</span>
        <div className="goal-card-header-info">
          <span className="goal-card-category">{app.label}</span>
          <span className="goal-card-type-tag">
            {goal.goal_type === 'short_term' ? '📅 Short-Term' : '🏆 Long-Term'}
          </span>
        </div>
        <DueChip targetDate={goal.target_date} status={goal.status} />
        {(userRole === 'coach' || userRole === 'admin') && (
          <button className="btn-delete-goal" onClick={e => { e.stopPropagation(); onDelete(goal.id); }} title="Delete">✕</button>
        )}
      </div>

      <div className="goal-card-body">
        <h3 className="goal-card-title">{goal.title}</h3>

        {(goal.start_value || goal.target_value) ? (
          <p className="goal-card-measure">
            <span className="measure-label">Measure:</span> {goal.current_value ?? goal.start_value ?? '—'} → {goal.target_value ?? '—'} {goal.unit || ''}
          </p>
        ) : goal.description ? (
          <p className="goal-card-measure">
            <span className="measure-label">Measure:</span> {goal.description}
          </p>
        ) : null}

        <div className="goal-card-meta">
          <span className={`status-pill ${statusClass[goal.status] || ''}`}>
            {statusLabel[goal.status] || goal.status}
          </span>
          <span className="difficulty-pill">
            {DIFFICULTY.find(d => d.id === goal.difficulty_level)?.icon}{' '}
            {DIFFICULTY.find(d => d.id === goal.difficulty_level)?.label}
          </span>
          {goal.target_date && (
            <span className="target-date-pill">
              📅 {new Date(goal.target_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </span>
          )}
        </div>

        <div className="progress-track">
          <div
            className="progress-fill"
            style={{ width: `${goal.progress_percentage}%`, backgroundColor: isAchieved ? '#27AE60' : app.color }}
          />
        </div>
        <p className="progress-pct">{goal.progress_percentage}% complete</p>

        {isAchieved && goal.achieved_date && (
          <p className="achieved-on">
            🏆 Achieved {new Date(goal.achieved_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
        )}
      </div>

      {expanded && (
        <div className="goal-card-expanded">
          {canEdit && !isAchieved && (
            <div className="progress-update-section">
              <label className="progress-update-label">
                Update your progress: <strong>{localPct}%</strong>
              </label>
              <div className="slider-row">
                <input
                  type="range" min="0" max="100" step="5"
                  value={localPct}
                  onChange={e => setLocalPct(parseInt(e.target.value))}
                  className="progress-slider"
                  style={{ accentColor: app.color }}
                />
                <button
                  className="btn-save-progress"
                  onClick={handleSave}
                  disabled={saving || localPct === goal.progress_percentage}
                  style={{ backgroundColor: app.color }}
                >
                  {saving ? 'Saving…' : localPct === 100 ? '🎉 Complete' : 'Save'}
                </button>
              </div>
              {localPct === 100 && (
                <p className="slider-hint">Setting to 100% marks this goal as achieved!</p>
              )}
            </div>
          )}

          {(() => {
            const strategies = asArr(goal.strategies);
            const obstacles = asArr(goal.obstacles);
            const hasAny = strategies.length || obstacles.length || goal.support || (goal.reminder_frequency && goal.reminder_frequency !== 'none');
            if (!hasAny) return null;
            const block = { margin: '0 0 10px' };
            const h = { margin: '0 0 4px', fontSize: '.85rem', fontWeight: 700, color: '#374151' };
            return (
              <div style={{ borderTop: '1px solid #eef2f7', paddingTop: 10, marginTop: 4 }}>
                {strategies.length > 0 && (
                  <div style={block}><h4 style={h}>✅ Strategies</h4>
                    <ul style={{ margin: 0, paddingLeft: 18, fontSize: '.85rem', color: '#475569' }}>{strategies.map((s, i) => <li key={i}>{s}</li>)}</ul></div>
                )}
                {goal.support && <div style={block}><h4 style={h}>🤝 Support</h4><p style={{ margin: 0, fontSize: '.85rem', color: '#475569' }}>{goal.support}</p></div>}
                {obstacles.length > 0 && (
                  <div style={block}><h4 style={h}>⚠️ Obstacles &amp; plan</h4>
                    <ul style={{ margin: 0, paddingLeft: 18, fontSize: '.85rem', color: '#475569' }}>
                      {obstacles.map((o, i) => <li key={i}><strong>{o.obstacle}</strong>{o.plan ? ` → ${o.plan}` : ''}</li>)}</ul></div>
                )}
                {goal.reminder_frequency && goal.reminder_frequency !== 'none' && (
                  <div style={block}><h4 style={h}>🔔 Reminder</h4><p style={{ margin: 0, fontSize: '.85rem', color: '#475569', textTransform: 'capitalize' }}>{goal.reminder_frequency}{goal.pinned ? ' · pinned on home' : ''}</p></div>
                )}
              </div>
            );
          })()}

          {goal.feedback && goal.feedback.length > 0 && (
            <div className="feedback-section">
              <h4 className="feedback-heading">💬 Coach Feedback</h4>
              {goal.feedback.map((f, i) => (
                <div key={i} className="feedback-item">
                  <div className="feedback-meta">
                    <span className="feedback-coach">{f.coach_name || 'Coach'}</span>
                    <span className="feedback-type">{
                      { great_job: '🌟 Great Job', keep_going: '💪 Keep Going', milestone: '🎉 Milestone', suggestion: '💡 Suggestion', general: '📝 Note' }[f.type] || '📝'
                    }</span>
                  </div>
                  <p className="feedback-text">{f.feedback}</p>
                </div>
              ))}
            </div>
          )}

          <button className="btn-collapse" onClick={onToggle}>▲ Collapse</button>
        </div>
      )}
    </div>
  );
}

// --- Main component ---
function GoalSetting() {
  const toast = useToast();
  const navigate = useNavigate();

  let user = null;
  try { user = JSON.parse(localStorage.getItem('ams_user') || localStorage.getItem('user') || 'null'); } catch {}
  const userRole = user?.role;
  const isStaff = ['coach', 'admin', 'superadmin', 'staff'].includes(userRole);

  const [athlete, setAthlete] = useState(null);
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [expandedGoalId, setExpandedGoalId] = useState(null);
  const [celebration, setCelebration] = useState(null);
  const blankGoal = () => ({
    title: '', description: '', goal_type: 'short_term', category: 'general', difficulty_level: 'intermediate',
    start_value: '', current_value: '', target_value: '', unit: '',   // M
    strategies: [''],                                                  // A
    is_realistic: false,                                              // R
    start_date: TODAY, target_date: '',                              // T
    reminder_frequency: 'weekly', pinned: true,                      // E
    support: '',                                                     // S
    obstacles: [{ obstacle: '', plan: '' }],                        // T (obstacles)
  });
  const [formData, setFormData] = useState(blankGoal);
  const setField = (patch) => setFormData(f => ({ ...f, ...patch }));
  // A — strategies list
  const setStrategy = (i, val) => setFormData(f => { const s = [...f.strategies]; s[i] = val; return { ...f, strategies: s }; });
  const addStrategy = () => setFormData(f => ({ ...f, strategies: [...f.strategies, ''] }));
  const removeStrategy = (i) => setFormData(f => ({ ...f, strategies: f.strategies.filter((_, x) => x !== i) }));
  // T — obstacles list ({obstacle, plan})
  const setObstacle = (i, key, val) => setFormData(f => { const o = [...f.obstacles]; o[i] = { ...o[i], [key]: val }; return { ...f, obstacles: o }; });
  const addObstacle = () => setFormData(f => ({ ...f, obstacles: [...f.obstacles, { obstacle: '', plan: '' }] }));
  const removeObstacle = (i) => setFormData(f => ({ ...f, obstacles: f.obstacles.filter((_, x) => x !== i) }));

  // Staff belong in the review center
  useEffect(() => {
    if (isStaff) navigate('/goals/review', { replace: true });
  }, [isStaff, navigate]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [goalsRes, athleteRes] = await Promise.all([
        api.get(`${API_BASE_URL}/api/v1/goals`),
        userRole === 'athlete' ? api.get(`${API_BASE_URL}/athletes/me`) : Promise.resolve({ data: null }),
      ]);
      setGoals(goalsRes.data || []);
      if (athleteRes.data) setAthlete(athleteRes.data);
    } catch (err) {
      console.error('Error loading goals:', err);
      toast('Error loading goals', 'error');
    } finally {
      setLoading(false);
    }
  }, [userRole, toast]);

  useEffect(() => { if (!isStaff) loadData(); }, [loadData, isStaff]);

  const handleAddGoal = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) return;
    try {
      const payload = {
        athlete_id: athlete?.id,
        ...formData,
        strategies: (formData.strategies || []).map(s => (s || '').trim()).filter(Boolean),
        obstacles: (formData.obstacles || [])
          .filter(o => (o.obstacle || '').trim())
          .map(o => ({ obstacle: o.obstacle.trim(), plan: (o.plan || '').trim() })),
      };
      await api.post(`${API_BASE_URL}/api/v1/goals`, payload);
      // Re-fetch so the new goal carries all its worksheet fields for the card.
      loadData();
      setFormData(blankGoal());
      setShowForm(false);
      toast('Goal set! Now go crush it! 🎯', 'success');
    } catch (err) {
      toast(err.response?.data?.error || 'Failed to create goal', 'error');
    }
  };

  const handleProgressUpdate = async (goalId, pct) => {
    try {
      const res = await api.put(`${API_BASE_URL}/api/v1/goals/${goalId}/progress`, { progress_percentage: pct });
      setGoals(prev => prev.map(g => {
        if (g.id !== goalId) return g;
        const updated = { ...g, progress_percentage: pct, status: res.data.new_status || g.status };
        if (res.data.achieved) updated.achieved_date = TODAY;
        return updated;
      }));
      if (res.data.achieved) {
        const goal = goals.find(g => g.id === goalId);
        setCelebration(goal?.title || 'Your Goal');
      } else {
        toast('Progress updated!', 'success');
      }
    } catch (err) {
      toast('Failed to update progress', 'error');
    }
  };

  const handleDeleteGoal = async (goalId) => {
    if (!window.confirm('Delete this goal?')) return;
    try {
      await api.delete(`${API_BASE_URL}/api/v1/goals/${goalId}`);
      setGoals(prev => prev.filter(g => g.id !== goalId));
      toast('Goal deleted', 'success');
    } catch (err) {
      toast('Failed to delete goal', 'error');
    }
  };

  const handleToggleExpand = async (goalId) => {
    if (expandedGoalId === goalId) { setExpandedGoalId(null); return; }
    setExpandedGoalId(goalId);
    const goal = goals.find(g => g.id === goalId);
    if (goal && goal.feedback === undefined) {
      try {
        const res = await api.get(`${API_BASE_URL}/api/v1/goals/${goalId}`);
        setGoals(prev => prev.map(g => g.id === goalId ? { ...g, feedback: res.data.feedback || [] } : g));
      } catch {}
    }
  };

  const grouped = {
    short_term: goals.filter(g => g.goal_type === 'short_term'),
    long_term:  goals.filter(g => g.goal_type === 'long_term'),
  };
  const achieved = goals.filter(g => g.status === 'achieved' || g.status === 'exceeded');
  const dueSoon  = goals.filter(g => {
    const d = daysUntil(g.target_date);
    return d !== null && d >= 0 && d <= 7 && g.status !== 'achieved' && g.status !== 'exceeded';
  });

  if (loading) {
    return <div className="goal-loading"><div className="goal-spinner" />Loading goals…</div>;
  }

  return (
    <div className="goal-setting-container">
      {celebration && <CelebrationOverlay title={celebration} onDismiss={() => setCelebration(null)} />}

      <div className="goal-page-header">
        <div>
          <h1 className="goal-page-title">My Goals</h1>
          <p className="goal-page-subtitle">SMART goals — Specific, Measurable, Achievable, Relevant, Time-bound</p>
        </div>
        {!showForm && (
          <button className="btn-new-goal" onClick={() => setShowForm(true)}>+ Set New Goal</button>
        )}
      </div>

      <div className="goal-stats-bar">
        <div className="goal-stat">
          <span className="goal-stat-num">{goals.length}</span>
          <span className="goal-stat-label">Total</span>
        </div>
        <div className="goal-stat">
          <span className="goal-stat-num">{grouped.short_term.length}</span>
          <span className="goal-stat-label">Short-Term</span>
        </div>
        <div className="goal-stat">
          <span className="goal-stat-num">{grouped.long_term.length}</span>
          <span className="goal-stat-label">Long-Term</span>
        </div>
        <div className="goal-stat goal-stat-green">
          <span className="goal-stat-num">{achieved.length}</span>
          <span className="goal-stat-label">Achieved</span>
        </div>
      </div>

      {dueSoon.length > 0 && (
        <div className="due-soon-strip">
          <span className="due-soon-icon">⏰</span>
          <span>
            <strong>{dueSoon.length} goal{dueSoon.length > 1 ? 's' : ''}</strong> due within 7 days:
            {' '}{dueSoon.map(g => `"${g.title}"`).join(', ')}
          </span>
        </div>
      )}

      {showForm && (
        <div className="smart-form-container">
          <div className="smart-form-header">
            <h2>Set a SMARTEST Goal</h2>
            <button className="btn-close-form" onClick={() => setShowForm(false)}>✕</button>
          </div>

          <form onSubmit={handleAddGoal}>
            {/* S — Set short & long-term */}
            <div className="smart-field">
              <div className="smart-label-row">
                <span className="smart-badge" style={{ background: '#4f46e5' }}>S</span>
                <label>Set the goal (and pick short or long-term)</label>
              </div>
              <input type="text" required className="smart-input"
                placeholder="e.g., Land my back handspring on beam without wobbling"
                value={formData.title} onChange={e => setField({ title: e.target.value })} />
              <div className="time-row" style={{ marginTop: 8 }}>
                <div className="time-field">
                  <label className="sub-label">Type</label>
                  <select className="smart-select" value={formData.goal_type} onChange={e => setField({ goal_type: e.target.value })}>
                    <option value="short_term">Short-Term (keeps you motivated)</option>
                    <option value="long_term">Long-Term (season / career)</option>
                  </select>
                </div>
                <div className="time-field">
                  <label className="sub-label">Area</label>
                  <select className="smart-select" value={formData.category} onChange={e => setField({ category: e.target.value })}>
                    {APPARATUS.map(app => <option key={app.id} value={app.id}>{app.emoji} {app.label}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {/* M — Measurable: where you are → where you want to be */}
            <div className="smart-field">
              <div className="smart-label-row">
                <span className="smart-badge" style={{ background: '#0891b2' }}>M</span>
                <label>Measurable — where you are now → where you want to be</label>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                <input className="smart-input" placeholder="Now (e.g. 2)" value={formData.start_value} onChange={e => setField({ start_value: e.target.value, current_value: formData.current_value || e.target.value })} />
                <input className="smart-input" placeholder="Currently" value={formData.current_value} onChange={e => setField({ current_value: e.target.value })} />
                <input className="smart-input" placeholder="Target (e.g. 8)" value={formData.target_value} onChange={e => setField({ target_value: e.target.value })} />
                <input className="smart-input" placeholder="Unit (landings)" value={formData.unit} onChange={e => setField({ unit: e.target.value })} />
              </div>
              <input className="smart-input" style={{ marginTop: 8 }} placeholder="How you'll know it's done (optional note)"
                value={formData.description} onChange={e => setField({ description: e.target.value })} />
            </div>

            {/* A — Achievement strategies */}
            <div className="smart-field">
              <div className="smart-label-row">
                <span className="smart-badge" style={{ background: '#16a34a' }}>A</span>
                <label>Achievement strategies — what you'll do to get there</label>
              </div>
              {formData.strategies.map((s, i) => (
                <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                  <input className="smart-input" placeholder="e.g. Attend every practice · conditioning 3×/wk · proper rest"
                    value={s} onChange={e => setStrategy(i, e.target.value)} />
                  {formData.strategies.length > 1 && <button type="button" className="btn-cancel" onClick={() => removeStrategy(i)}>✕</button>}
                </div>
              ))}
              <button type="button" className="btn-cancel" onClick={addStrategy}>+ Add strategy</button>
            </div>

            {/* R — Realistic */}
            <div className="smart-field">
              <div className="smart-label-row">
                <span className="smart-badge" style={{ background: '#d97706' }}>R</span>
                <label>Realistic — is this within your current ability?</label>
              </div>
              <div className="difficulty-buttons">
                {DIFFICULTY.map(d => (
                  <button key={d.id} type="button"
                    className={`diff-btn ${formData.difficulty_level === d.id ? 'active' : ''}`}
                    onClick={() => setField({ difficulty_level: d.id })}>{d.icon} {d.label}</button>
                ))}
              </div>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, fontSize: '.9rem' }}>
                <input type="checkbox" checked={formData.is_realistic} onChange={e => setField({ is_realistic: e.target.checked })} />
                Yes — this is challenging but achievable with my training.
              </label>
            </div>

            {/* T — Time-bound */}
            <div className="smart-field">
              <div className="smart-label-row">
                <span className="smart-badge" style={{ background: '#dc2626' }}>T</span>
                <label>Time-bound — by when?</label>
              </div>
              <input type="date" className="smart-input" min={TODAY}
                value={formData.target_date} onChange={e => setField({ target_date: e.target.value })} />
            </div>

            {/* E — Exhibit (reminders) */}
            <div className="smart-field">
              <div className="smart-label-row">
                <span className="smart-badge" style={{ background: '#7c3aed' }}>E</span>
                <label>Exhibit — keep it in front of you</label>
              </div>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: '.9rem', marginBottom: 8 }}>
                <input type="checkbox" checked={formData.pinned} onChange={e => setField({ pinned: e.target.checked })} />
                Pin this goal on my home screen
              </label>
              <div className="time-field">
                <label className="sub-label">Remind me</label>
                <select className="smart-select" value={formData.reminder_frequency} onChange={e => setField({ reminder_frequency: e.target.value })}>
                  <option value="none">No reminders</option>
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Every 2 weeks</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
            </div>

            {/* S — Seek support */}
            <div className="smart-field">
              <div className="smart-label-row">
                <span className="smart-badge" style={{ background: '#0d9488' }}>S</span>
                <label>Seek support — who will help you?</label>
              </div>
              <input className="smart-input" placeholder="e.g. My coach, my beam group, my parents"
                value={formData.support} onChange={e => setField({ support: e.target.value })} />
            </div>

            {/* T — Target obstacles */}
            <div className="smart-field">
              <div className="smart-label-row">
                <span className="smart-badge" style={{ background: '#be123c' }}>T</span>
                <label>Target obstacles — what might get in the way, and your plan</label>
              </div>
              {formData.obstacles.map((o, i) => (
                <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                  <input className="smart-input" placeholder="Obstacle (e.g. sore wrists)" value={o.obstacle} onChange={e => setObstacle(i, 'obstacle', e.target.value)} />
                  <input className="smart-input" placeholder="If it happens, I'll… (e.g. tape + ice)" value={o.plan} onChange={e => setObstacle(i, 'plan', e.target.value)} />
                  {formData.obstacles.length > 1 && <button type="button" className="btn-cancel" onClick={() => removeObstacle(i)}>✕</button>}
                </div>
              ))}
              <button type="button" className="btn-cancel" onClick={addObstacle}>+ Add obstacle</button>
            </div>

            <div className="form-actions">
              <button type="button" className="btn-cancel" onClick={() => setShowForm(false)}>Cancel</button>
              <button type="submit" className="btn-submit-goal">Set Goal 🎯</button>
            </div>
          </form>
        </div>
      )}

      {grouped.short_term.length > 0 && (
        <section className="goals-section">
          <h2 className="goals-section-title">Short-Term Goals</h2>
          <div className="goals-grid">
            {grouped.short_term.map(goal => (
              <GoalCard
                key={goal.id} goal={goal} userRole={userRole}
                expanded={expandedGoalId === goal.id}
                onToggle={() => handleToggleExpand(goal.id)}
                onProgressUpdate={handleProgressUpdate}
                onDelete={handleDeleteGoal}
              />
            ))}
          </div>
        </section>
      )}

      {grouped.long_term.length > 0 && (
        <section className="goals-section">
          <h2 className="goals-section-title">Long-Term Goals</h2>
          <div className="goals-grid">
            {grouped.long_term.map(goal => (
              <GoalCard
                key={goal.id} goal={goal} userRole={userRole}
                expanded={expandedGoalId === goal.id}
                onToggle={() => handleToggleExpand(goal.id)}
                onProgressUpdate={handleProgressUpdate}
                onDelete={handleDeleteGoal}
              />
            ))}
          </div>
        </section>
      )}

      {goals.length === 0 && !showForm && (
        <div className="goals-empty-state">
          <div className="empty-rocket">🚀</div>
          <h3>No goals yet</h3>
          <p>Start your journey by setting your first SMART goal.</p>
          <button className="btn-new-goal" onClick={() => setShowForm(true)}>Set Your First Goal</button>
        </div>
      )}
    </div>
  );
}

export default GoalSetting;
